import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { fmtLocalDateTime } from '../../utils/datetime';

/**
 * Settings ▸ Metrc ▸ Metrc Incoming — LAYOUT port of the legacy XAF
 * MetrcIncoming_DetailView (owner request 2026-10-13, image 502): a From/To date
 * range + a Facility picker + Export + the incoming grid.
 *
 * ⚠️ The legacy form is a LIVE METRC state-API integration: the Facility list is
 * fetched from the METRC API (MetrcController.getfacility) and picking a Facility +
 * date range calls the METRC API to fetch the incoming transfers (a non-persistent
 * BO parsed from the payload). AI has NO METRC API integration (no client,
 * endpoints or credentials), so — per the owner's "build the layout only" choice —
 * the Facility picker and the live transfer fetch are NOT wired. This page shows the
 * local incoming history (dbo.MetrcIncomingHistory, 0 live rows) via the existing
 * generic list endpoint, filtered by the Received Date range client-side. Frontend
 * only; no API/DB change. When the METRC API integration lands, wire the Facility
 * list + the live fetch here.
 */

interface Row {
  oid: string;
  receivedDate: string | null;
  hashCode: number | string | null;
  dataPreview: string | null;
}

export default function MetrcIncomingPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/modules/set-metrc-incoming',
        { params: { page: 1, pageSize: 500 }, silentLoading: true } as never);
      setRows((res.data as { data: Row[] }).data ?? []);
    } catch {
      notify.error('Failed to load Metrc Incoming.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // Client-side From/To filter on Received Date (the live Facility + API fetch are not
  // available in AI — see the header note). Inclusive of the whole To day.
  const filtered = useMemo(() => {
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    if (fromMs == null && toMs == null) return rows;
    return rows.filter((r) => {
      if (!r.receivedDate) return false;
      const t = new Date(r.receivedDate).getTime();
      if (Number.isNaN(t)) return false;
      if (fromMs != null && t < fromMs) return false;
      if (toMs != null && t > toMs) return false;
      return true;
    });
  }, [rows, from, to]);

  const cols: AppColumn<Row>[] = [
    { field: 'receivedDate', header: 'Received Date', width: 180, text: (r) => (r.receivedDate ? fmtLocalDateTime(r.receivedDate) : '') },
    { field: 'hashCode', header: 'Hash', width: 140, text: (r) => (r.hashCode == null ? '' : String(r.hashCode)) },
    { field: 'dataPreview', header: 'Data', text: (r) => r.dataPreview ?? '' },
  ];

  const labelStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--ai-ink)' };
  const labelCell: CSSProperties = { width: 70, fontWeight: 600 };
  const inputStyle: CSSProperties = {
    height: 26, boxSizing: 'border-box', border: '1px solid var(--ai-line)', borderRadius: 0,
    padding: '0 8px', fontSize: 13, color: 'var(--ai-ink)', background: '#fff', minWidth: 190,
  };

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          {/* subgroup node (owner request 2026-10-14) */}
          <span>Metrc</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Metrc Incoming</span>
        </div>
        <FormTitle pageId="set-metrc-incoming" title="Metrc Incoming" loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load} refreshing={loading}
            exportData={{ filename: 'metrc-incoming', ...gridExportMatrix(filtered, cols) }}
          />
        </div>

        {/* Filter block — From / To / Facility, matching the legacy MetrcIncoming_DetailView. */}
        <div className="bl-card" style={{ padding: '14px 18px', marginBottom: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={labelStyle}>
              <span style={labelCell}>From:</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <span style={labelCell}>To:</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <span style={labelCell}>Facility:</span>
              <input type="text" value="" placeholder="N/A" disabled readOnly
                title="The METRC facility list is fetched from the live METRC API, which is not integrated in AI yet."
                style={{ ...inputStyle, background: 'var(--ai-header-bg, #f6f7f9)', cursor: 'not-allowed' }} />
            </label>
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<Row>
            rows={filtered} columns={cols} getRowId={(r) => r.oid}
            loading={loading} loadMs={loadMs}
            emptyText="No data to display"
          />
        </div>
      </div>
    </div>
  );
}
